export interface DocumentType {
  id: string
  nameEn: string
  nameEs: string
}

/**
 * Tipos combinados (KILA-365): un mismo archivo que trae dos documentos dentro.
 * Se ofrecen al cargar y al extraer, pero en el archivo se guardan como el tipo
 * principal + `additionalFileTypes`, para que todo lo que filtra por fileType
 * (comparación de documentos, validación de facturas, checklist) siga sirviendo.
 */
export const COMBINED_INVOICE_PACKING_LIST = 'commercial_invoice+packing_list'

export const COMBINED_DOCUMENT_TYPES: Record<string, { fileType: string; additionalFileTypes: string[] }> = {
  [COMBINED_INVOICE_PACKING_LIST]: {
    fileType: 'commercial_invoice',
    additionalFileTypes: ['packing_list']
  }
}

/**
 * Facturas de agente de carga / aduana: mismo flujo de extracción y misma vista
 * que la factura de terceros, solo cambia la categoría en la que caen los cargos.
 */
export const THIRD_PARTY_INVOICE_TYPES = ['third_party_invoice', 'freight_agent_invoice', 'customs_agent_invoice']

export const isThirdPartyInvoiceType = (id: string | null | undefined): boolean =>
  !!id && THIRD_PARTY_INVOICE_TYPES.includes(id)

export const isCombinedDocumentType = (id: string): boolean =>
  Object.prototype.hasOwnProperty.call(COMBINED_DOCUMENT_TYPES, id)

/** Lo elegido en el selector → lo que se guarda en el documento del archivo. */
export const splitCombinedFileType = (id: string): { fileType: string; additionalFileTypes: string[] } =>
  COMBINED_DOCUMENT_TYPES[id] ?? { fileType: id, additionalFileTypes: [] }

/** Inverso: el archivo ya guardado → el id del tipo que hay que preseleccionar. */
export const combinedFileTypeId = (fileType: string, additionalFileTypes?: string[] | null): string => {
  if (!additionalFileTypes?.length) return fileType
  const match = Object.entries(COMBINED_DOCUMENT_TYPES).find(([, combined]) =>
    combined.fileType === fileType &&
    combined.additionalFileTypes.length === additionalFileTypes.length &&
    combined.additionalFileTypes.every(type => additionalFileTypes.includes(type))
  )
  return match ? match[0] : fileType
}

export const DOCUMENT_TYPES: DocumentType[] = [
  {
    id: 'commercial_invoice',
    nameEn: 'Commercial Invoice',
    nameEs: 'Factura comercial'
  },
  {
    id: COMBINED_INVOICE_PACKING_LIST,
    nameEn: 'Commercial Invoice + Packing List',
    nameEs: 'Factura comercial + Lista de empaque'
  },
  {
    id: 'third_party_invoice',
    nameEn: 'Third Party Invoice',
    nameEs: 'Factura de terceros'
  },
  {
    id: 'freight_agent_invoice',
    nameEn: 'Freight Forwarder Invoice',
    nameEs: 'Factura agente de carga'
  },
  {
    id: 'customs_agent_invoice',
    nameEn: 'Customs Broker Invoice',
    nameEs: 'Factura agente de aduana'
  },
  {
    id: 'bill_of_lading',
    nameEn: 'Bill of Lading (B/L)',
    nameEs: 'Conocimiento de embarque master'
  },
  {
    id: 'house_bill_of_lading',
    nameEn: 'House Bill of Lading (HBL)',
    nameEs: 'Conocimiento de embarque hijo (HBL)'
  },
  {
    id: 'purchase_order',
    nameEn: 'Purchase Order',
    nameEs: 'Orden de compra'
  },
  {
    id: 'packing_list',
    nameEn: 'Packing List',
    nameEs: 'Lista de empaque'
  },
  {
    id: 'certificate_of_origin',
    nameEn: 'Certificate of Origin',
    nameEs: 'Certificado de origen'
  },
  {
    id: 'proforma_invoice',
    nameEn: 'Proforma Invoice',
    nameEs: 'Factura proforma'
  },
  {
    id: 'insurance_certificate',
    nameEn: 'Insurance Certificate',
    nameEs: 'Certificado de seguro'
  },
  {
    id: 'export_license',
    nameEn: 'Export License',
    nameEs: 'Licencia de exportación'
  },
  {
    id: 'drfi',
    nameEn: 'DRFI',
    nameEs: 'Declaración DRFI'
  },
  {
    id: 'import_license',
    nameEn: 'Import License',
    nameEs: 'Licencia de importación'
  },
  {
    id: 'letter_of_credit',
    nameEn: 'Letter of Credit (L/C)',
    nameEs: 'Carta de crédito'
  },
  {
    id: 'master_air_waybill',
    nameEn: 'Master Air Waybill (MAWB)',
    nameEs: 'Guía aérea master (MAWB)'
  },
  {
    id: 'house_air_waybill',
    nameEn: 'House Air Waybill (HAWB)',
    nameEs: 'Guía aérea house (HAWB)'
  },
  {
    id: 'house_air_waybill_courier',
    nameEn: 'House Air Waybill Courier (HAWB Courier)',
    nameEs: 'Guía Aérea Courier (HAWB Courier)'
  },
  {
    id: 'courier_original_docs',
    nameEn: 'Courier Original Documents (DOX)',
    nameEs: 'Guía Courier Documentos Originales (DOX)'
  },
  {
    id: 'sea_waybill',
    nameEn: 'Sea Waybill',
    nameEs: 'Guía marítima (no negociable)'
  },
  {
    id: 'road_consignment_note',
    nameEn: 'Road Consignment Note (CMR)',
    nameEs: 'Carta de porte por carretera (CMR)'
  },
  {
    id: 'rail_waybill',
    nameEn: 'Rail Waybill',
    nameEs: 'Guía ferroviaria'
  },
  {
    id: 'dangerous_goods_declaration',
    nameEn: 'Dangerous Goods Declaration (DGD)',
    nameEs: 'Declaración de mercancías peligrosas'
  },
  {
    id: 'inspection_certificate',
    nameEn: 'Inspection Certificate',
    nameEs: 'Certificado de inspección'
  },
  {
    id: 'fumigation_certificate',
    nameEn: 'Fumigation Certificate',
    nameEs: 'Certificado de fumigación'
  },
  {
    id: 'customs_declaration',
    nameEn: 'Customs Declaration',
    nameEs: 'Declaración de aduana'
  },
  {
    id: 'customs_report',
    nameEn: 'Customs Report',
    nameEs: 'Reporte de Aduana'
  },
  {
    id: 'tax_report',
    nameEn: 'Tax Report',
    nameEs: 'Reporte de Tributos'
  },
  {
    id: 'dim',
    nameEn: 'Import Declaration (DIM)',
    nameEs: 'CO - Declaración de Importación (DIM)'
  },
  {
    id: 'import_declaration',
    nameEn: 'Import Declaration',
    nameEs: 'PA - Declaración de Importación'
  },
  {
    id: 'pedimento',
    nameEn: 'MX - Pedimento (Import Declaration)',
    nameEs: 'MX - Pedimento'
  },
  {
    id: 'export_declaration',
    nameEn: 'Export Declaration (DEX)',
    nameEs: 'CO - Declaración de Exportación (DEX)'
  },
  {
    id: 'delivery_order',
    nameEn: 'Delivery Order',
    nameEs: 'Orden de entrega'
  },
  {
    id: 'warehouse_receipt',
    nameEn: 'Warehouse Receipt',
    nameEs: 'Recibo de almacén'
  },
  {
    id: 'export_packing_declaration',
    nameEn: 'Export Packing Declaration',
    nameEs: 'Declaración de empaque para exportación'
  },
  // Documentos específicos OTM
  {
    id: 'otm_instructions',
    nameEn: 'OTM Instructions / Service Request',
    nameEs: 'Instrucciones OTM / Solicitud de Servicio'
  },
  {
    id: 'freight_certification',
    nameEn: 'Freight Certification',
    nameEs: 'Certificación de Fletes'
  },
  {
    id: 'msds',
    nameEn: 'Safety Data Sheet (MSDS)',
    nameEs: 'Hoja de Seguridad (MSDS)'
  },
  {
    id: 'technical_sheet',
    nameEn: 'Technical Data Sheet',
    nameEs: 'Ficha Técnica de Mercancía'
  },
  {
    id: 'quotation',
    nameEn: 'Quotation',
    nameEs: 'Cotización'
  },
  {
    id: 'serials',
    nameEn: 'Serial Numbers',
    nameEs: 'Seriales'
  },
  {
    id: 'inventory_inquiry',
    nameEn: 'Inventory Inquiry',
    nameEs: 'Consulta de Inventario'
  },
  {
    id: 'pin',
    nameEn: 'PIN',
    nameEs: 'Retiro de contenedores'
  },
  {
    id: 'dav',
    nameEn: 'DAV',
    nameEs: 'Declaración Andina de Valor'
  },
  {
    id: 'cpi',
    nameEn: 'CPI',
    nameEs: 'Carta Porte Internacional'
  },
  {
    id: 'cubicaje_final',
    nameEn: 'Final Cubicage',
    nameEs: 'Cubicaje Final'
  },
  {
    id: 'cargo_manifest',
    nameEn: 'Cargo Manifest',
    nameEs: 'Manifiesto de Carga'
  },
  {
    id: 'import_liquidation',
    nameEn: 'Import Liquidation',
    nameEs: 'Liquidación de importación'
  },
  {
    id: 'import_pre_liquidation',
    nameEn: 'Import Pre-Liquidation',
    nameEs: 'Preliquidación de importación'
  },
  {
    id: 'generated_from_template',
    nameEn: 'Generated from Template',
    nameEs: 'Generado desde plantilla'
  },
  {
    id: 'other',
    nameEn: 'Other',
    nameEs: 'Otro'
  }
]

/**
 * Obtiene el nombre del tipo de documento según el idioma
 */
export const getDocumentTypeName = (documentTypeId: string, locale: string = 'es'): string => {
  const docType = DOCUMENT_TYPES.find(dt => dt.id === documentTypeId)
  if (!docType) return documentTypeId
  return locale === 'en' ? docType.nameEn : docType.nameEs
}

/**
 * Obtiene el tipo de documento por ID
 */
export const getDocumentType = (documentTypeId: string): DocumentType | undefined => {
  return DOCUMENT_TYPES.find(dt => dt.id === documentTypeId)
}
